import { EventEmitter, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Product as ProductEntity } from '@shared/classes/product.class';
import { ICustomerDisplayProvider } from './ICustomerDisplayProvider';
import { EmailModelChanged, ICustomerDisplayService } from './ICustomerDisplayService';
import { ICustomerDisplayDevice } from './ICustomerDisplayDevice';
import { Invoice } from '@shared/classes/invoice.class';
import { ICustomerDisplaySession } from './ICustomerDisplaySession';
import { SecurityService } from '@core/services/system/security.service';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { Image, IMAGE_SIZES } from '@shared/classes/image.class';
import { ImageLoaderService } from '@shared/components/ionic-image-loader-v5';
import { ImageUtils } from '@core/services/utils/image.utils';
import { DbDaoService } from '@core/services/db/db-dao.service';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { Company } from '@shared/classes/company.class';
import { getRandomProducts, ImageSize } from './CustomerDisplayServiceUtils';
import { Customer } from '@shared/classes/customer.class';
import { TranslateService } from '@ngx-translate/core';
import { CartService } from '@core/services/system/cart/cart.service';
import { InvoiceEntry, InvoiceGuest, Product } from './entities';
import { ICustomerDisplaySessionDelegate } from './ICustomerDisplaySessionDelegate';
import { Subject, Subscription } from 'rxjs';
import { auditTime, distinctUntilChanged } from 'rxjs/operators';
import { CustomerDisplayServiceState } from './state';
import { UniversalCustomerDisplayProvider } from './universal/UniversalCustomerDisplayProvider';
import { InvoiceEntry as InvoiceEntryEntity } from '@shared/classes/invoice-entry.class';
import { IDeviceReportingService } from '@core/services/system/device-report';
import { CustomerDisplayReporting } from './CustomerDisplayReporting';
import { LogService } from '@core/services/system/logger';
import PQueue from 'p-queue';
import { ignorePromiseError } from '@core/services/utils/promise.utils';
import { ErrorLevel } from '@paymash/logging';

@Injectable({ providedIn: 'root' })
export class CustomerDisplayService extends ICustomerDisplayService implements ICustomerDisplaySessionDelegate {
  private readonly logger = this.logService.createLogger('CustomerDisplayService');

  private readonly providers: ICustomerDisplayProvider[];

  private devices_: ICustomerDisplayDevice[] = [];
  private readonly devicesChangeEvent_: EventEmitter<ICustomerDisplayDevice[]>;

  private readonly sessions: ICustomerDisplaySession[] = [];

  private readonly emailModelChangedSubject = new Subject<EmailModelChanged>();

  private readonly sessionsSubscriptions = new Map<ICustomerDisplaySession, Subscription[]>();

  private readonly report: CustomerDisplayReporting;

  private _state: CustomerDisplayServiceState | undefined;
  private turnedOn: boolean;
  private isDeviceDiscoveryStarted: boolean;

  /**
   * Cache of base64-encoded images keyed by their resolved URL.
   *
   * Why: every change in the cart re-runs `processInvoiceChangedInternal`,
   * which previously called `imageLoaderService.getImagePath` +
   * `imageUtils.imageToDataURL` for **every** cart entry on every tick.
   * The resulting base64 blobs were then shipped through the Capacitor
   * bridge inside the `cart` message (visible in the WebKit hangs as
   * `IPC::Connection::sendMessage` / `WKKeyedCoder` / `CloneDeserializer`).
   * For the typical 1-display setup the same product image is re-sent
   * dozens of times per shopping session — caching makes this a no-op
   * after the first read.
   */
  private readonly imageBase64Cache = new Map<string, string | undefined>();
  private static readonly IMAGE_CACHE_LIMIT = 256;
  // Byte ceiling for the base64 cache. The count limit alone (256) does not bound memory:
  // 256 NORMAL (300x300) base64 strings can reach tens of MB and stay resident for the
  // whole session, which is a real risk against the iOS WKWebView memory limit.
  private static readonly IMAGE_CACHE_MAX_BYTES = 8 * 1024 * 1024;
  private imageCacheBytes = 0;

  /** Signature of the last cart payload actually shipped to the display, to
   *  short-circuit redundant bridge calls when the invoice mutates without
   *  semantic change (object rebuilt, modificationDate bumped only). */
  private lastSentCartSignature: string | undefined;

  constructor(
    injector: Injector,
    private readonly securityService: SecurityService,
    private readonly localStorage: LocalStorage,
    private readonly imageLoaderService: ImageLoaderService,
    private readonly imageUtils: ImageUtils,
    private readonly dbDaoService: DbDaoService,
    private readonly translateService: TranslateService,
    private readonly cartService: CartService,
    deviceReportingService: IDeviceReportingService,
    private readonly logService: LogService
  ) {
    super();

    this.logger.info('Initialize customer display service');

    this.report = new CustomerDisplayReporting(this, deviceReportingService);

    this.devicesChangeEvent_ = new EventEmitter<ICustomerDisplayDevice[]>();

    this.providers = [new UniversalCustomerDisplayProvider(injector, this.report)];

    this.providers.forEach((provider) => {
      provider.devices.subscribe((devices) => this.onDevicesUpdated(provider, devices));
      provider.initialize(this);
    });

    // Cart updates are coalesced before going to the bridge.
    //
    //  - `auditTime(150)`: cashier interactions (add item, change quantity,
    //    edit guest, toggle discount) typically fire `activeInvoiceUpdated`
    //    several times within a few dozen ms. We collapse them into a single
    //    bridge call. 150 ms is well below human-perceived latency for a
    //    secondary display but eliminates the burst.
    //  - `distinctUntilChanged` on a lightweight signature: if the produced
    //    invoice is structurally identical to the previous one (same object
    //    rebuilt, no real diff) we skip the work entirely — no native call,
    //    no base64 re-encoding, no `WebUserContentControllerProxy::didPostMessage`.
    //
    // For the rare 2+ displays case this still works: the signature does
    // not depend on the destination session.
    //
    // The signature is snapshotted at emission (keySelector): CartService mutates and re-emits the
    // same Invoice instance, so comparing live signatures of the stored reference always matched.
    toObservable(this.cartService.activeInvoiceUpdated)
      .pipe(
        auditTime(150),
        distinctUntilChanged(
          (prev: string, cur: string) => prev === cur,
          (invoice: Invoice | undefined) => this.invoiceSourceSignature(invoice)
        )
      )
      .subscribe((data) => {
        this.processInvoiceChange(data).catch(ignorePromiseError);
      });
  }

  /**
   * Cheap structural fingerprint of an `Invoice` for the purposes of
   * deduplicating cart updates pushed to the customer display. Includes
   * only fields that the display actually renders.
   */
  private invoiceSourceSignature(invoice: Invoice | undefined): string {
    if (!invoice) return 'none';
    const entries = invoice.getActiveInvoiceEntries?.() ?? [];
    // Keep the per-entry tuple small — uuid + qty + price + name + guest is
    // enough to detect any user-visible change.
    const entriesSig = entries
      .map((e: any) => `${e?.uuid}:${e?.quantity}:${e?.price}:${e?.name}:${e?.guestNumber}:${e?.isGuest ? 1 : 0}`)
      .join('|');
    return [
      invoice.uuid,
      invoice.amount,
      invoice.discount,
      invoice.discountPercentage,
      invoice.customerDiscount,
      invoice.customerDiscountPercentage,
      invoice.customer?.uuid ?? '',
      entriesSig,
    ].join('#');
  }

  get devices() {
    return this.devices_;
  }

  get devicesChangeEvent() {
    return this.devicesChangeEvent_;
  }

  async turnOn() {
    if (this.turnedOn) {
      return;
    }
    this.turnedOn = true;

    for (const provider of this.providers) {
      await provider.turnOn();
    }

    // Eager discoveries (the built-in in-app secondary display on Sunmi/Android) are
    // zero-config peripherals with no iOS mDNS/Network.framework crash surface: the display
    // stays in Android's default mirror mode until InAppDiscovery finds it and we auto-connect
    // (enable('customer')). It is a temporary device that is only saved after connecting once,
    // so gating it behind a saved device deadlocks it into mirroring forever. Always run it.
    this.providers.forEach((provider) => provider.startEagerDiscoveries());

    // The lazy network (Zeroconf/mDNS) discovery is started only when there is a saved/known
    // device to (re)connect to. turnOn() runs after restoreConnectedDevices(), so a non-empty
    // list here means the merchant has paired a display. Installs without a customer display
    // used to keep the resolver running all session — pure waste and an iOS Network.framework
    // crash trigger (sa_dst_cache_create). The pairing screen starts discovery on demand.
    if (this.devices_.length > 0) {
      this.startDeviceDiscovery();
    } else {
      this.logger.debug('No saved customer display, skipping network device discovery until needed');
    }

    this.logger.info('Customer display service turned on');
  }

  async turnOff() {
    if (!this.turnedOn) {
      return;
    }
    this.turnedOn = false;

    this.stopDeviceDiscovery();

    for (const provider of this.providers) {
      await provider.turnOff();
    }

    await this.disconnectAllDevices();

    // Drop caches so the next session gets a clean push and we do not
    // hold base64 blobs in memory while the feature is off.
    this.imageBase64Cache.clear();
    this.imageCacheBytes = 0;
    this.lastSentCartSignature = undefined;

    this.logger.info('Customer display service turned off');
  }

  startDeviceDiscovery() {
    if (this.isDeviceDiscoveryStarted) {
      return;
    }
    this.isDeviceDiscoveryStarted = true;

    this.providers.forEach((provider) => provider.startDeviceDiscovery());

    this.logger.debug('Device discovery started');
  }

  stopDeviceDiscovery() {
    if (!this.isDeviceDiscoveryStarted) {
      return;
    }
    this.isDeviceDiscoveryStarted = false;

    this.providers.forEach((provider) => provider.stopDeviceDiscovery());

    this.logger.debug('Device discovery stopped');
  }

  async connectDevice(device: ICustomerDisplayDevice) {
    const provider = this.findProviderById(device.providerId);
    await provider.connect(device);
    this.report.deviceConnected(device);
    this.logger.info('Device connected successfully', { device });
  }

  async disconnectDevice(device: ICustomerDisplayDevice) {
    const provider = this.findProviderById(device.providerId);
    const session = this.findSessionByDevice(device);
    await provider.disconnect(device, session);
    this.report.deviceDisconnected(device);
    this.logger.info('Device disconnected successfully', { device });
  }

  async disconnectAllDevices() {
    const devices = [...this.devices_];
    for (const device of devices) {
      if (device.status === 'disconnected') {
        continue;
      }
      await this.disconnectDevice(device);
    }
    this.logger.info('All devices disconnected', { devices });
  }

  async onDeviceConnected(session: ICustomerDisplaySession) {
    const device = session.device;
    this.logger.info('Device connected', { device });
    this.sessions.push(session);
    await this.setupSessionSubscriptions(session);
    await this.initializeDevice(session);
  }

  async onDeviceDisconnected(device: ICustomerDisplayDevice, session: ICustomerDisplaySession | undefined) {
    this.logger.info('Device disconnected', { device });

    if (!session) {
      return;
    }

    const index = this.sessions.indexOf(session);
    if (index > -1) {
      this.sessions.splice(index, 1);
    }

    await this.destroySessionSubscriptions(session);
    await session.destroy();
  }

  async processInvoiceChange(invoice: Invoice | undefined) {
    await this.processInvoiceChangedInternal(invoice, undefined, true);
  }

  async invoiceSent() {
    await this.invoiceSentInternal(undefined, true);
  }

  async invoiceSentInternal(sessions: ICustomerDisplaySession[] | undefined, setState: boolean) {
    this.logger.debug('Invoice sent', { setState });
    if (setState) {
      this.state = { type: 'invoiceSent' };
    }
    await this.processSessions((session) => session.invoiceSent(), sessions);
  }

  async lockUserInput(sessions?: ICustomerDisplaySession[]) {
    this.logger.debug('Lock user input');
    await this.processSessions((session) => session.lockUserInput(), sessions);
  }

  async unlockUserInput(sessions?: ICustomerDisplaySession[]) {
    this.logger.debug('Unlock user input');
    await this.processSessions((session) => session.unlockUserInput(), sessions);
  }

  async requestEmailAddress(amount: number, qrCode: string) {
    await this.requestEmailAddressInternal(amount, qrCode, undefined, true);
  }

  async setEmailAddress(value: string) {
    await this.setEmailAddressInternal(value, undefined, true);
  }

  // The email address itself is customer PII and never goes to the log stream.
  async setEmailAddressInternal(value: string, sessions: ICustomerDisplaySession[] | undefined, saveState: boolean) {
    if (saveState) {
      if (this.state && this.state.type === 'email') {
        this.state = {
          ...this.state,
          email: value,
        };
      }
    }

    await this.processSessions((session) => session.setEmailAddress(value), sessions);
  }

  emailModelChanged() {
    return this.emailModelChangedSubject;
  }

  private onDevicesUpdated(provider: ICustomerDisplayProvider, devices: ICustomerDisplayDevice[]) {
    const availableDevices: ICustomerDisplayDevice[] = [...this.devices_];

    devices.forEach((device) => {
      const index = availableDevices.findIndex((d) => d.equals(device));
      if (index < 0) {
        availableDevices.push(device);
      }
    });

    const devicesToRemove = availableDevices.filter(
      (device) => device.providerId === provider.providerId && devices.findIndex((d) => d.equals(device)) < 0
    );
    devicesToRemove.forEach((device) => availableDevices.splice(availableDevices.indexOf(device), 1));

    this.setDevices(availableDevices);
  }

  private setDevices(devices: ICustomerDisplayDevice[]) {
    this.devices_ = devices;
    this.devicesChangeEvent_.emit(devices);
  }

  private findProviderById(providerId: string) {
    const provider = this.providers.find((p) => p.providerId === providerId);
    if (!provider) throw new Error(`Unknown provider: ${providerId}`);
    return provider;
  }

  private findSessionByDevice(device: ICustomerDisplayDevice) {
    return this.sessions.find((s) => s.device.equals(device));
  }

  private async initializeDevice(session: ICustomerDisplaySession) {
    // company
    const { company, currency } = this.getCompanyAndCurrency();
    const lang = this.getPreferredLanguage();
    const companyImage = await this.loadImageAsync(company.image, IMAGE_SIZES.MEDIUM);
    this.logger.debug('Initialize session', { currency, lang, companyImageExists: companyImage !== undefined });
    await session.initialize(currency, lang, companyImage);

    // 3 random products
    const products = await this.getRandomProductsFromDatabaseWithImages();
    await session.setProductsForScreensaver(products);

    this.logger.debug('Restore state', { stateType: this.state?.type });

    // cart
    if (this.state?.type === 'default') {
      await this.processInvoiceChangedInternal(this.state.invoice, [session], false);
    }

    // email
    if (this.state?.type === 'email') {
      await this.requestEmailAddressInternal(this.state?.amount, this.state.qrCode, [session], false);
      await this.setEmailAddressInternal(this.state.email, [session], false);
    }

    // invoiceSent
    if (this.state?.type === 'invoiceSent') {
      await this.invoiceSentInternal([session], false);
    }
  }

  private async setupSessionSubscriptions(session: ICustomerDisplaySession) {
    let subscriptions: Subscription[] = this.sessionsSubscriptions.get(session);
    if (!subscriptions) {
      subscriptions = [];
      this.sessionsSubscriptions.set(session, subscriptions);
    }

    subscriptions.push(
      session.emailModelChanged().subscribe((data) => {
        if (this.state?.type === 'email') {
          this.state = {
            ...this.state,
            email: data.emailAddress,
          };
        }
        this.emailModelChangedSubject.next(data);
      })
    );
  }

  private async destroySessionSubscriptions(session: ICustomerDisplaySession) {
    const subscriptions: Subscription[] = this.sessionsSubscriptions.get(session);
    if (!subscriptions?.length) return;

    subscriptions.forEach((session) => {
      session.unsubscribe();
    });

    this.sessionsSubscriptions.delete(session);
  }

  private async processInvoiceChangedInternal(
    invoice: Invoice | undefined,
    sessions: ICustomerDisplaySession[] | undefined,
    setState: boolean
  ) {
    if (setState) {
      this.state = {
        type: 'default',
        invoice,
      };
    }

    if (!this.sessions?.length) {
      return;
    }

    // customer
    const customer = invoice?.customer ? await this.getCustomerByUuid(invoice.customer.uuid) : undefined;
    if (customer) {
      const customerName = customer.dataToShowInList;
      const customerImage = await this.loadImageAsync(customer.image, IMAGE_SIZES.NORMAL);
      await this.processSessions((session) => session.addCustomer(customerName, customerImage), sessions);
    } else {
      await this.processSessions(async (session) => session.removeCustomer(), sessions);
    }

    // entries
    if (!invoice) {
      // Keep the dedupe signature in sync with what was actually pushed. Without this the reset's
      // empty cart leaves lastSentCartSignature pointing at the previous (non-empty) payload, so a
      // following push that renders identically to that previous payload (e.g. a copied invoice that
      // matches the just-paid one) is wrongly skipped here as "unchanged".
      if (sessions === undefined) {
        this.lastSentCartSignature = this.buildCartPayloadSignature([], [], 0);
      }
      await this.processSessions(async (session) => session.updateInvoiceEntries([], [], 0), sessions);
      return;
    }

    const invoiceEntities = invoice.getActiveInvoiceEntries();
    const rawEntries = await Promise.all(
      invoiceEntities.map(async (entry) => {
          const image = await this.loadImageAsync(entry.image, IMAGE_SIZES.NORMAL);
          return { entry, image };
        })
    );

    const { entries, guests } = this.groupGuests(rawEntries);

    const addDiscount = ([prefix, postfix]: ['invoice' | 'customer', 'cash' | 'percent'], totalPrice: number) =>
      totalPrice &&
      entries.push(
        InvoiceEntry.createDiscount(
          prefix === 'invoice' ? this.translateService.instant('invoice_discount') : this.translateService.instant('customer_discount'),
          totalPrice,
          prefix === 'invoice' ? invoice.totalInvoiceDiscountAmount : invoice.totalCustomerDiscountAmount,
          `${prefix}-discount-${postfix}`
        )
      );

    addDiscount(['invoice', 'cash'], invoice.discount);
    addDiscount(['invoice', 'percent'], invoice.discountPercentage);
    addDiscount(['customer', 'cash'], invoice.customerDiscount);
    addDiscount(['customer', 'percent'], invoice.customerDiscountPercentage);

    const guestList = Array.from(guests.values());

    // Final dedupe before crossing the bridge. We hash the *built* payload
    // (post-grouping, post-discount-injection) so even semantic-noise
    // updates that survived the upstream `distinctUntilChanged` (e.g.
    // image base64 became available, modificationDate-only bumps) are
    // dropped here when they do not actually change what the display
    // would render.
    const cartPayloadSignature = this.buildCartPayloadSignature(entries, guestList, invoice?.amount);
    if (sessions === undefined && cartPayloadSignature === this.lastSentCartSignature) {
      return;
    }
    if (sessions === undefined) {
      this.lastSentCartSignature = cartPayloadSignature;
    }

    // Sizes only: the entries themselves are far too heavy to log on every cart change,
    // but without any breadcrumb a stale display cannot be told from a skipped push.
    this.logger.debug('Cart pushed to display', { entries: entries.length, guests: guestList.length, amount: invoice?.amount });

    await this.processSessions((session) => session.updateInvoiceEntries(entries, guestList, invoice?.amount), sessions);
  }

  /**
   * Stable hash of the cart message body. We deliberately avoid
   * `JSON.stringify` of full `entries`/`guests` because they may contain
   * the (large) base64 image strings — comparing those is what we are
   * trying to *avoid*. Instead, hash only identity + visible state.
   */
  private buildCartPayloadSignature(entries: InvoiceEntry[], guests: InvoiceGuest[], amount: number | undefined): string {
    const e = entries
      .map((x: any) => `${x?.uuid ?? x?.id ?? ''}:${x?.quantity ?? ''}:${x?.price ?? ''}:${x?.name ?? ''}:${x?.guestUuid ?? ''}:${x?.image ? 'i' : ''}`)
      .join('|');
    const g = guests.map((x: any) => `${x?.uuid ?? ''}:${x?.guestNumber ?? ''}`).join('|');
    return `${amount ?? ''}#${e}#${g}`;
  }

  private groupGuests(rawEntries: { entry: InvoiceEntryEntity; image: string | undefined }[]) {
    const guests = new Map<number, InvoiceGuest>();
    const entries = new Array<InvoiceEntry>();

    rawEntries.forEach(({ entry, image }) => {
      if (entry.isGuest) {
        const guest = InvoiceGuest.fromInvoiceEntryEntity(entry);
        guests.set(guest.guestNumber, guest);
        return;
      }

      const guest = guests.get(entry.guestNumber);
      const entity = InvoiceEntry.fromInvoiceEntryEntity(entry, image, guest?.uuid);
      entries.push(entity);
    });

    return { entries, guests };
  }

  private async getCustomerByUuid(uuid: string): Promise<Customer | undefined> {
    try {
      const { data, status } = await this.dbDaoService.getDataByUUID(UPDATES_TYPES.Customer.type, uuid);
      if (!(data && status === 200)) return undefined;
      return new Customer(data);
    } catch (e) {
      return undefined;
    }
  }

  private async getRandomProductsFromDatabaseWithImages() {
    const maxCount = 50;
    // Cap parallel image loads: 50 concurrent disk-read + base64-encode chains produced a
    // large memory/CPU spike on every display (re)connect. Screensaver images don't need to
    // be instant, so load them in small waves — the 50 still complete, just not all at once.
    const concurrency = 4;
    const dbPageSize = 100;

    const queue = new PQueue({ concurrency });

    const result = new Array<Product>();

    await getRandomProducts(
      this.dbDaoService,
      async (product, cancel) => {
        if (result.find((p) => p.uniqueIdentifier === product.uuid) !== undefined) {
          return;
        }
        if (queue.pending >= concurrency) {
          await queue.onIdle();
        }
        queue
          .add(async () => {
            if (result.length >= maxCount) {
              cancel();
              return;
            }
            const image = await this.loadProductImageAsync(product, IMAGE_SIZES.NORMAL);
            if (!image) return;
            if (result.length >= maxCount) {
              cancel();
              return;
            }
            const productWithImage = Product.fromProductEntity(product, image);
            result.push(productWithImage);
          })
          .catch(ignorePromiseError);
      },
      { pageSize: dbPageSize }
    );

    await queue.onIdle();

    return result;
  }

  private getCompanyAndCurrency(): { company: Company; currency: string } {
    const company = this.securityService.getLoggedCompanyData();
    const currency = company['locale']['currency'];
    return { company, currency };
  }

  private getPreferredLanguage(): string {
    return this.localStorage.get('preferredLanguage');
  }

  private async loadProductImageAsync(product: ProductEntity, imageSize: ImageSize) {
    const image = product?.images?.length > 0 ? product.images[0].image : undefined;
    if (!image) return undefined;
    return this.loadImageAsync(image, imageSize);
  }

  private loadImageAsync(image: Image | undefined | null, imageSize: ImageSize): Promise<string | undefined> {
    if (!image) return Promise.resolve(undefined);
    const imageUrl = image.getImageUrlBySize(imageSize);
    if (!imageUrl) return Promise.resolve(undefined);

    // Hot-path cache: the same product/customer/company image is loaded
    // many times per session and the resulting base64 is sent to the
    // customer display verbatim. Re-reading from disk + re-encoding +
    // re-shipping the same bytes through the Capacitor bridge was a major
    // contributor to the iOS WK-IPC main-thread hangs (`CloneDeserializer`,
    // `WKKeyedCoder.cxx_construct`, `HTTPHeaderMap`).
    if (this.imageBase64Cache.has(imageUrl)) {
      return Promise.resolve(this.imageBase64Cache.get(imageUrl));
    }

    const base64RegularForReplace = /^data:.*.;base64,/;
    return this.imageLoaderService
      .getImagePath(imageUrl)
      .then((path) => this.imageUtils.convertFileSrc(path))
      .then((path) => this.imageUtils.imageToDataURL(path))
      .then((dataUrl) => (dataUrl ? dataUrl.replace(base64RegularForReplace, '') : undefined))
      .catch(() => undefined)
      .then((encoded) => {
        this.cacheImageBase64(imageUrl, encoded);
        return encoded;
      });
  }

  private cacheImageBase64(key: string, value: string | undefined) {
    // Crude FIFO eviction — Map preserves insertion order. We don't need
    // true LRU here; the working set is small (visible cart entries +
    // a handful of customer/company logos). Evict by both entry count and
    // total bytes so a few large images can't blow past the memory ceiling.
    const size = value ? value.length : 0;
    while (
      this.imageBase64Cache.size > 0 &&
      (this.imageBase64Cache.size >= CustomerDisplayService.IMAGE_CACHE_LIMIT ||
        this.imageCacheBytes + size > CustomerDisplayService.IMAGE_CACHE_MAX_BYTES)
    ) {
      const firstKey = this.imageBase64Cache.keys().next().value;
      if (firstKey === undefined) break;
      const evicted = this.imageBase64Cache.get(firstKey);
      this.imageCacheBytes -= evicted ? evicted.length : 0;
      this.imageBase64Cache.delete(firstKey);
    }
    this.imageBase64Cache.set(key, value);
    this.imageCacheBytes += size;
  }

  private async processSessions(
    callback: (session: ICustomerDisplaySession) => Promise<void>,
    sessions: ICustomerDisplaySession[] | undefined
  ) {
    if (sessions === undefined) sessions = [...this.sessions];
    for (const session of sessions) {
      try {
        await callback(session);
      } catch (e) {
        this.logger.error(e, 'Customer display session update failed', { level: ErrorLevel.Medium }, { device: session.device });
      }
    }
  }

  private async requestEmailAddressInternal(
    amount: number,
    qrCode: string,
    sessions: ICustomerDisplaySession[] | undefined,
    setState: boolean
  ) {
    this.logger.info('Request email address', { amount, setState });
    if (setState) {
      this.state = { type: 'email', amount, qrCode, email: '' };
    }
    await this.processSessions((session) => session.requestEmailAddress(amount, qrCode), sessions);
  }

  private get state() {
    return this._state;
  }

  private set state(newState: CustomerDisplayServiceState | undefined) {
    this._state = newState;
  }
}
